import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Wallet, Plus, CheckCircle2, FileDown, Loader2, Trash2, Receipt } from "lucide-react";
import { api, formatCurrency } from "@/lib/api";
import { toast } from "sonner";

const STATUS = {
  pending_confirmation: { lbl: "Da confermare", cls: "bg-amber-100 text-amber-800" },
  confirmed: { lbl: "Confermato", cls: "bg-emerald-100 text-emerald-800" },
};
const METHODS = ["bonifico", "contanti", "paypal", "stripe", "altro"];

export default function SaasPayments() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agencies, setAgencies] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ agency_id: "", amount: "", method: "bonifico", note: "", type: "manual" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: pays }, { data: ags }] = await Promise.all([
        api.get("/superadmin/payments"),
        api.get("/superadmin/agencies"),
      ]);
      setData(pays);
      setAgencies(ags || []);
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadReceipt = async (id) => {
    try {
      const res = await api.get(`/superadmin/payments/${id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `ricevuta_${id.slice(0, 8)}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore download"); }
  };

  const confirmPay = async (id) => {
    if (!window.confirm("Confermare questo pagamento?")) return;
    try { await api.put(`/superadmin/payments/${id}/confirm`); toast.success("Confermato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const deletePay = async (id) => {
    if (!window.confirm("Eliminare questo record?")) return;
    try { await api.delete(`/superadmin/payments/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const createPay = async () => {
    if (!form.agency_id || !form.amount) { toast.error("Agenzia e importo obbligatori"); return; }
    setBusy(true);
    try {
      await api.post("/superadmin/payments", { ...form, amount: Number(form.amount) });
      toast.success("Pagamento registrato"); setAddOpen(false);
      setForm({ agency_id: "", amount: "", method: "bonifico", note: "", type: "manual" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  if (loading || !data) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-r from-violet-700 to-indigo-700 text-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/superadmin")} className="hover:bg-white/10 rounded p-1" data-testid="back-to-sa">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <Wallet className="h-5 w-5" />
            <div className="leading-tight">
              <div className="font-display font-semibold">Cronologia pagamenti SaaS</div>
              <div className="text-[11px] text-violet-200/90">Super-Admin</div>
            </div>
          </div>
          <Button onClick={() => setAddOpen(true)} data-testid="add-payment" className="bg-white text-violet-700 hover:bg-violet-50">
            <Plus className="h-4 w-4 mr-1" /> Registra pagamento
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-4" data-testid="payments-page">
        {/* Totali */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="py-4">
              <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Totale incassato</p>
              <p className="text-2xl font-bold text-emerald-800 mt-1">{formatCurrency(data.confirmed_total)}</p>
              <p className="text-xs text-emerald-700/70">{data.confirmed_count} pagamenti confermati</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="py-4">
              <p className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Da confermare</p>
              <p className="text-2xl font-bold text-amber-800 mt-1">{formatCurrency(data.pending_total)}</p>
              <p className="text-xs text-amber-700/70">{data.pending_count} richieste in attesa</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Receipt className="h-5 w-5" /> Storico pagamenti</CardTitle></CardHeader>
          <CardContent className="p-0">
            {data.items.length === 0 ? (
              <div className="text-center py-10 text-slate-400 text-sm">Nessun pagamento registrato.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                    <tr>
                      <th className="text-left px-4 py-2.5">Data</th>
                      <th className="text-left px-3 py-2.5">Agenzia</th>
                      <th className="text-right px-3 py-2.5">Importo</th>
                      <th className="text-left px-3 py-2.5">Metodo</th>
                      <th className="text-center px-3 py-2.5">Stato</th>
                      <th className="text-left px-3 py-2.5">Ricevuta n°</th>
                      <th className="text-right px-4 py-2.5">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((p) => {
                      const st = STATUS[p.status] || { lbl: p.status, cls: "bg-slate-100 text-slate-700" };
                      return (
                        <tr key={p.id} className="border-t border-slate-100" data-testid={`pay-${p.id}`}>
                          <td className="px-4 py-3 text-xs font-mono">{new Date(p.created_at).toLocaleDateString("it-IT")}<br /><span className="text-[10px] text-slate-400">{new Date(p.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span></td>
                          <td className="px-3 py-3"><div className="font-medium">{p.agency_name}</div><div className="text-xs text-slate-500">{p.owner_email}</div></td>
                          <td className="px-3 py-3 text-right font-semibold">{formatCurrency(p.amount || 0)}</td>
                          <td className="px-3 py-3 text-xs">{p.method || "—"}</td>
                          <td className="px-3 py-3 text-center"><span className={`text-[10px] px-2 py-0.5 rounded ${st.cls}`}>{st.lbl}</span></td>
                          <td className="px-3 py-3 text-xs font-mono text-slate-600">{p.receipt_number || "—"}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex gap-1.5">
                              {p.status === "pending_confirmation" && (
                                <Button size="sm" data-testid={`confirm-${p.id}`} onClick={() => confirmPay(p.id)} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs">
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> Conferma
                                </Button>
                              )}
                              {p.status === "confirmed" && (
                                <Button size="sm" variant="outline" data-testid={`receipt-${p.id}`} onClick={() => downloadReceipt(p.id)} className="h-7 text-xs">
                                  <FileDown className="h-3 w-3 mr-1" /> Ricevuta
                                </Button>
                              )}
                              <Button size="sm" variant="outline" data-testid={`del-pay-${p.id}`} onClick={() => deletePay(p.id)} className="h-7 text-xs text-rose-600 hover:bg-rose-50">
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog Aggiungi pagamento */}
      <Dialog open={addOpen} onOpenChange={(o) => !o && setAddOpen(false)}>
        <DialogContent className="max-w-sm" data-testid="add-payment-dialog">
          <DialogHeader><DialogTitle>Registra pagamento manuale</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <div>
              <Label>Agenzia <span className="text-red-500">*</span></Label>
              <select data-testid="pay-agency" className="w-full h-10 rounded border border-slate-300 px-3 text-sm" value={form.agency_id} onChange={(e) => setForm({ ...form, agency_id: e.target.value })}>
                <option value="">Seleziona…</option>
                {agencies.filter((a) => !a.is_main).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label>Importo (€) *</Label>
                <Input data-testid="pay-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div>
                <Label>Metodo</Label>
                <select data-testid="pay-method" className="w-full h-10 rounded border border-slate-300 px-3 text-sm bg-white" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}>
                  {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label>Nota</Label>
              <Textarea data-testid="pay-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Riferimento bonifico, periodo coperto, ecc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Annulla</Button>
            <Button data-testid="pay-save" onClick={createPay} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Registra
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
